'use client'
import { homePageStyle as style } from './home-page.style';
import { Visual } from './visual';
import { NonSportProductProps, ProductsWide, ThirdPartyProductProps } from './products-wide';
import { Action } from './action';
import { ForgetPassword } from '../forget-password/forget-password';
import { useEffect, useState, useMemo } from 'react';
import { useTemplateResource } from '@/hooks/query/template-resource';
import { TemplateResourceContext } from './context/template-resource-context';
import { athenaMenuService } from '@/services/athena-menu.service';
import { useAthenaMenuSetting } from '@/hooks/query/athena';
import { useAthenaMenuStore } from '@/store/athena-menu';
import { MenuTemplateComponentEnum } from '@/hooks/query/athena/types';
import { getEventName, MarketData, useHomePageProductsOfEventModes } from '@/hooks/query';
import { EventMode, menuDefinition, ProvidedMarket } from '@/types/menu-setting';
import { BreakpointDisplayWrapper } from '@/features/breakpoint/components';
import { Breakpoint } from '@/features/breakpoint';
import clsx from 'clsx';
import { MobileOptions } from './mobile/mobile-options';
import { MobileActivities } from './mobile/mobile-activities';
import { ProductsWithMarkets, TemplateProducts, ThirdPartyProducts } from './mobile/mobile-products';
import { MobileNotification } from './mobile/mobile-notification';
import { Banners } from '@/features/home-page/banners';
import { homeStyle as mobileStyle } from './mobile/home-page.style';
import { Header } from '@/components/layout/header';
import { Appbar } from '@/features/appbar';
import { ViewEnum } from '@/types';
import { useAppearanceStore } from '@/store/appearance';
import { Sidebar } from '@/features/sidebar';
import { SportsbookLayoutContainer } from '@/components/layout/sportsbook-layout-container';
import { TopMenu } from '@/features/top-menu';
import { useParams } from 'next/navigation';
import { sportService } from '@/services/sport.service';
import { isGalaxyThirdPartyTopMenuExcludedSport, isNumberGames, sortVirtualSports } from '@/services/settings.service';
import { useAuthToken } from '@/store/auth';
import { MemberMobileLogin } from '../member-login/member-mobile-login';
import { productStyle } from './products-wide/product.style';
import { useLicSettings } from '@/domains/config/hooks/use-lic-settings';

const productDataReducer = (matchCountOfMarkets: Record<string, number>, marketData: MarketData): Record<string, number> => {
	Object.keys(matchCountOfMarkets).forEach(market => {
		const matchCountOfSport = marketData[market as ProvidedMarket] ?? 0;
		if (matchCountOfSport > 0) {
			matchCountOfMarkets[market as ProvidedMarket] += matchCountOfSport;	
		}
	});
	return matchCountOfMarkets;
};

export const MainHomePage = () => {
	const [productId, setProductId] = useState<number>();
	const [productType, setProductType] = useState('Sports');
	const { templateResource, isSuccess, isFetching } = useTemplateResource(
		productType,
		productId,
	);
	const { sport } = useParams();
	const isNumberGame = isNumberGames(Number(sport));
	const isAnyVirtualSports = sportService.isAnyVirtualSportsProduct(Number(sport));
	const [activeTab] = useAthenaMenuStore(state => [state.activeTab]);
	const { data: menuSetting } = useAthenaMenuSetting();
	const activeSetting = menuSetting?.MainMenuList.find(m => m.TabName === activeTab);
	const homePageProductsOfEventMode = useHomePageProductsOfEventModes();
	const [currentEventMode, setCurrentEventMode] = useState<EventMode | undefined>();
	const [matchCountOfMarkets, setMatchCountOfMarkets] = useState<Record<string, number> | undefined>(undefined);
	const [showLoading, setShowLoading] = useState<boolean>(true);
	const [currentMarket, setCurrentMarket] = useState<string | undefined>(undefined);
	const [thirdPartyProducts, setThirdPartyProducts] = useState<ThirdPartyProductProps[] | undefined>(undefined);
	const [nonSportProducts, setNonSportProducts] = useState<Array<NonSportProductProps>>([]);
	const [availableEventModes, setAvailableEventModes] = useState<EventMode[]>([]);
	const [isContributor, setIsContributor] = useState<boolean | undefined>(undefined);
	const { view } = useAppearanceStore(state => state);
	const { enabledBetBuilderPlusSportIDs = [], nonSport } = useLicSettings();

	const commingSoccerName = useMemo(() => {
		const homePageProducts = homePageProductsOfEventMode[currentEventMode as EventMode] || {};
		const soccerItem = Object.values(homePageProducts)
			.flatMap(marketData => marketData)
			.find(item => item.GameId === 1);
		return soccerItem?.Name ?? 'Soccer';
	}, [currentEventMode, homePageProductsOfEventMode]);

	useEffect(() => {
		const order = Object.keys(menuDefinition);
		const currentAvailableEventModes = Object.keys(homePageProductsOfEventMode)
			.map(eventMode => eventMode as EventMode)
			.sort((a, b) => order.indexOf(a) - order.indexOf(b));
		if (availableEventModes.length !== currentAvailableEventModes.length || currentAvailableEventModes.some(currentAvailableEventMode => availableEventModes.indexOf(currentAvailableEventMode) === -1)) {
			setAvailableEventModes(currentAvailableEventModes)
		}
	}, [homePageProductsOfEventMode]);

	useEffect(() => {
		if (!currentEventMode || !homePageProductsOfEventMode[currentEventMode as EventMode]) {
			setMatchCountOfMarkets(undefined);
			return;
		}
		
		const homePageProducts = homePageProductsOfEventMode[currentEventMode as EventMode];
		const parlayProductData = homePageProducts.p ?? [];
		const betBuilderPlusProductData = homePageProducts.betBuilderPlus?.filter(marketData => enabledBetBuilderPlusSportIDs?.includes(marketData.GameId)) ?? [];
		const matchDistributionOfParlayProducts = parlayProductData.reduce(productDataReducer, { 'E': 0, 'T': 0, 'L': 0 } as Record<string, number>);
		const matchDistributionOfBetBuilderPlusProducts = betBuilderPlusProductData.reduce(productDataReducer, { 'E': 0, 'T': 0, 'L': 0 } as Record<string, number>);
		const productData = homePageProducts.n ?? [];
		const sumOfParlayProducts = ['E', 'L', 'T'].reduce((sum, market) => sum += matchDistributionOfParlayProducts[market], 0);
		const sumOfBetBuilderPlusProducts = ['E', 'L', 'T'].reduce((sum, market) => sum += matchDistributionOfBetBuilderPlusProducts[market], 0);
		
		const baseMarketCounts: Record<string, number> = { 'E': 0, 'T': 0, 'L': 0, 'P': sumOfParlayProducts };
		
		baseMarketCounts['BB'] = sumOfBetBuilderPlusProducts;
		
		const currentHomePageProducts = productData.reduce(productDataReducer, baseMarketCounts);

		if (!matchCountOfMarkets || Object.entries(currentHomePageProducts).some(pair => {
			const [eventMode, count] = pair;
			return matchCountOfMarkets[eventMode as EventMode] !== count;
		})) {
			setMatchCountOfMarkets(currentHomePageProducts);
		}
		
		if (homePageProductsOfEventMode[currentEventMode] && showLoading) {
			setShowLoading(false);
		}
	}, [currentEventMode, homePageProductsOfEventMode, enabledBetBuilderPlusSportIDs]);

	useEffect(() => {
		if (!productId && isSuccess && templateResource?.ProductResources.length > 0) {
			setCurrentEventMode('M0');
		}
	}, [productId, isSuccess]);

	useEffect(() => {
		const extractNumber = (input: string) => {
			const match = input.match(/\d+(-\d+)?/);
			return match ? match[0] : null;
		}

		if (thirdPartyProducts) {
		
			let nonSportProductsTemp = thirdPartyProducts.map(product => {
				const nonSportProduct: NonSportProductProps = {
					eventMode: product.eventMode,
					GameId: product.GameId,
					GlobalResourceLabel: product.GlobalResourceLabel,
					BackgroundName: product.BackgroundName,
					Count: product.Count,
					RouteTarget: product.GameId === 'virtual180'
						? sportService.resolveVirtual180RouteTarget(product.RouteTarget, nonSport?.virtualSoccerList)
						: product.RouteTarget,
					IsLive: product.IsLive,
					forAnalyse: product.forAnalyse,
				};
				const gameId = product.GameId;
				const sportId = typeof(gameId)=='number' ? gameId:extractNumber(gameId);
				if (sportId) {
					const targetResource = templateResource.SubProductResources.find(subProductResource => 
						subProductResource.SportId?.toLocaleLowerCase() == sportId.toString().toLocaleLowerCase()
					);
					if (targetResource) {
						nonSportProduct.Url = targetResource.ClassName;
						nonSportProduct.Name = targetResource.Name;
						nonSportProduct.Sort = targetResource.Sort;
					}
				}
				return nonSportProduct;
			});
		
			nonSportProductsTemp = sortVirtualSports(nonSportProductsTemp.map(product => ({ GameId: product.GameId })))
				.map(sortedItem => nonSportProductsTemp.find(product => product.GameId === sortedItem.GameId)!);
			setNonSportProducts(nonSportProductsTemp);
		}
	}, [templateResource?.SubProductResources, nonSport?.virtualSoccerList]);

	const homePageProducts = homePageProductsOfEventMode[currentEventMode as EventMode];
	const parlayProductData = (!homePageProducts || !homePageProducts.p) ? [] : homePageProducts.p;
	const betBuilderPlusProductData = (!homePageProducts || !homePageProducts.betBuilderPlus) ? [] : homePageProducts.betBuilderPlus;
	const productData = (!homePageProducts || !homePageProducts.n) ? [] : homePageProducts.n;

	return (
		<>
			<TemplateResourceContext.Provider value={{
				productId: productId,
				setProductId: setProductId,
				productType: productType,
				setProductType: setProductType,
				templateResource: {
					ProductResources: templateResource?.ProductResources? templateResource.ProductResources: [],
					SubProductResources: templateResource?.SubProductResources? templateResource.SubProductResources: [],
					VisualResources: templateResource?.VisualResources? templateResource.VisualResources: [],
					TemplateName: templateResource?.TemplateName? templateResource.TemplateName: ''
				},
				isSuccess: isSuccess,
				isFetching: isFetching
			}}
			>
				<BreakpointDisplayWrapper minBreakpoint={Breakpoint.md}>
					{athenaMenuService.hasTemplateComponent(activeSetting, MenuTemplateComponentEnum.Visual2) ? <Visual /> : null }
					<div data-name="jupiterHome" className={style.jupiterHome}>
						{
							!matchCountOfMarkets ? null : 
								(
									<>
										<section data-name="product" className={productStyle.product}>
											<ProductsWide 
												key={productType}
												currentEventMode={currentEventMode}
												currentMarket={currentMarket}
												setCurrentMarket={(market: string) => setCurrentMarket(market)}
												matchCountOfMarkets={matchCountOfMarkets}
												currentRoutePrefix={!currentEventMode ? undefined : currentEventMode === 'M0' ? 'sports' : `world/${getEventName(currentEventMode)}`}
												availableProducts={
													(!currentMarket || !currentEventMode || !homePageProductsOfEventMode[currentEventMode]) ? []
														: currentMarket === 'P' ? 
															parlayProductData.filter(marketData => ['E', 'L', 'T'].some(market => (marketData[market as ProvidedMarket] ?? 0) > 0))
															: currentMarket === 'BB' ? 
																betBuilderPlusProductData.filter(marketData => ['E', 'L', 'T'].some(market => (marketData[market as ProvidedMarket] ?? 0) > 0))
																: productData.filter(marketData => (marketData[currentMarket as ProvidedMarket] ?? 0) > 0)
												}
												commingSoccerName={commingSoccerName}
												setThirdPartyProduct={(productProps) => setThirdPartyProducts(productProps)}
												availableEventModes={availableEventModes}
												thirdPartyProducts={thirdPartyProducts?thirdPartyProducts:[]}
												setCurrentEventMode={(eventMode: EventMode) => {
													if (!Array.isArray(thirdPartyProducts)) {
														setThirdPartyProducts(undefined);
													}
													setCurrentEventMode(eventMode);
												}}
												setIsContributor={setIsContributor}
												isContributor={isContributor}
												nonSportProducts={nonSportProducts}
												setNonSportProducts={(nonSportProductProps) => setNonSportProducts(nonSportProductProps)}
											/>
										</section>
									</>									
								)
						}
						<Banners />
						<Action />
					</div>
					<ForgetPassword />
				</BreakpointDisplayWrapper>
				<BreakpointDisplayWrapper maxBreakpoint={Breakpoint.sm}>
					<SportsbookLayoutContainer>
						<Header />	
						{view === ViewEnum.galaxy && (isNumberGame || isAnyVirtualSports) && isGalaxyThirdPartyTopMenuExcludedSport(Number(sport)) ? null : 
							(
								<>
									{view !== ViewEnum.mars ? <TopMenu /> : null}
									<Sidebar />
								</>
							)
						}
						<div className={clsx('home', mobileStyle.root)}>
							<div className={mobileStyle.homeWrapper}>
								<Banners />
								<MobileNotification />
								<MobileOptions
									availableEventModes={availableEventModes}
									setThirdPartyProduct={(productProps) => setThirdPartyProducts(productProps)}
									setCurrentEventMode={(eventMode: EventMode) => {
										// setShowLoading(true);
										// setTimeout(() => setCurrentEventMode(eventMode), LOADING_TIME);
										if (Array.isArray(thirdPartyProducts)) {
											setThirdPartyProducts(undefined);
										}
										setCurrentEventMode(eventMode)
									}}
									setIsContributor={setIsContributor}
									setNonSportProducts={setNonSportProducts}
								/>
								<MobileActivities />
								{
									isContributor && matchCountOfMarkets ? (
										<ProductsWithMarkets 
											isLoading={showLoading}
											currentEventMode={currentEventMode}
											currentMarket={currentMarket}
											currentRoutePrefix={!currentEventMode ? undefined : currentEventMode === 'M0' ? 'sports' : `world/${getEventName(currentEventMode)}`}
											matchCountOfMarkets={matchCountOfMarkets}
											setCurrentMarket={(market: string) => {
												// setShowLoading(true);
												// setTimeout(() => setCurrentMarket(market), LOADING_TIME);
												setCurrentMarket(market)
											}}
											commingSoccerName={commingSoccerName}
											availableProducts={
												(!currentMarket || !currentEventMode || !homePageProductsOfEventMode[currentEventMode]) ? []
													: currentMarket === 'P' ? 
														parlayProductData.filter(marketData => ['E', 'L', 'T'].some(market => (marketData[market as ProvidedMarket] ?? 0) > 0))
														: currentMarket === 'BB' ? 
															betBuilderPlusProductData.filter(marketData => ['E', 'L', 'T'].some(market => (marketData[market as ProvidedMarket] ?? 0) > 0))
															: productData.filter(marketData => (marketData[currentMarket as ProvidedMarket] ?? 0) > 0)
											}
										/>): isSuccess ? 
										(Array.isArray(nonSportProducts) && nonSportProducts.length > 0) || (Array.isArray(thirdPartyProducts) && thirdPartyProducts.length > 0) ? 
											<ThirdPartyProducts productProps={nonSportProducts} /> : 
											<TemplateProducts templateResource={templateResource} /> : 
										null
								}
							</div>
						</div>
						<Appbar />
					</SportsbookLayoutContainer>
				</BreakpointDisplayWrapper>
			</TemplateResourceContext.Provider>
		</>		
	)
}

export const HomePage = () => {
	const authToken = useAuthToken();
	const isBefore = !authToken;

	return isBefore?(
		<>
			<BreakpointDisplayWrapper minBreakpoint={Breakpoint.md}>
				<MainHomePage />
			</BreakpointDisplayWrapper>
			<BreakpointDisplayWrapper maxBreakpoint={Breakpoint.sm}>
				<MemberMobileLogin />
			</BreakpointDisplayWrapper>
		</>
	):(
		<MainHomePage />
	)
}